/** Batch "Redeliver" dialog for the delivery-logs grid (a custom `dialog(ctx)`
 *  batch action) — the console counterpart of
 *  `manage.py event_delivery bulk-redeliver`.
 *
 *  Each selected log row's event is rebuilt from its S3 archive and re-enters
 *  the normal match→render→dispatch pipeline; rows without an archive are
 *  skipped (reported as per-row errors in Background Tasks). Optional inputs:
 *  a run tag (defaults to a server-generated `console[<series id>]` shared by
 *  the whole run) and a delivery-method subset (default: all methods).
 */
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import type { BatchActionContext } from '@/components/DataGrid/batchTypes';

/** Event_Delivery_Log.log_tag is a CharField(128). */
const MAX_TAG_LENGTH = 128;

/** The method names the backend maps to canonical codes (same set as the CLI's
 *  --methods flag). Unchecked-all = no restriction (all methods). */
const METHODS = [
  { key: 'email', label: 'Email' },
  { key: 'sms', label: 'SMS' },
  { key: 'chat', label: 'Chat' },
  { key: 'whatsapp', label: 'WhatsApp' },
] as const;

export function RedeliverDialog({ ctx }: { ctx: BatchActionContext }) {
  const [tag, setTag] = useState('');
  const [checked, setChecked] = useState<Set<string>>(new Set());

  const trimmedTag = tag.trim();
  const tagTooLong = trimmedTag.length > MAX_TAG_LENGTH;

  const toggle = (key: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const submit = () => {
    if (tagTooLong) return;
    const params: Record<string, unknown> = {};
    if (trimmedTag) params.tag = trimmedTag;
    if (checked.size > 0) params.methods = METHODS.filter((m) => checked.has(m.key)).map((m) => m.key);
    void ctx.runStandard(params);
    ctx.close();
  };

  return (
    <Dialog open onClose={ctx.close} fullWidth maxWidth="sm">
      <DialogTitle>Redeliver</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="warning">
            Redelivery re-sends real notifications to guests. Each selected delivery is rebuilt
            from its archive and re-enters the normal delivery pipeline — capped at 1,000 rows
            per run. Runs in the background; rows without an archive are skipped.
          </Alert>

          <TextField
            size="small"
            label="Run tag (optional)"
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            error={tagTooLong}
            helperText={
              tagTooLong
                ? `Max ${MAX_TAG_LENGTH} characters.`
                : 'Stamped on the new deliveries (tag=… in the search). Defaults to a generated console[…] tag shared by this run.'
            }
          />

          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Restrict to delivery methods (none checked = all methods):
            </Typography>
            <FormGroup row>
              {METHODS.map((m) => (
                <FormControlLabel
                  key={m.key}
                  control={<Checkbox size="small" checked={checked.has(m.key)} onChange={() => toggle(m.key)} />}
                  label={m.label}
                />
              ))}
            </FormGroup>
          </Box>

          {ctx.selection.mode === 'all_matching' && (
            <Alert severity="info">
              Acting on {ctx.matchedPhrase} delivery logs (up to the
              1,000-row cap).
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={ctx.close}>Cancel</Button>
        <Button variant="contained" color="warning" disabled={tagTooLong} onClick={submit}>
          Redeliver {ctx.count.toLocaleString()} delivery log(s)
        </Button>
      </DialogActions>
    </Dialog>
  );
}
